export const ENTRIES_UNDO_SCHEMA_VERSION = 1 as const;

export type EntryUndoMetadata = {
	object: "entry_undo_metadata";
	receipt: string;
	version: typeof ENTRIES_UNDO_SCHEMA_VERSION;
	action: string;
	label: string;
	affectedEntryGroupIds: string[];
	affectedEntryIds: string[];
};

export type EntryUndoRecord = EntryUndoMetadata & {
	id: string;
	createdAt: string;
};

export type EntryUndoHistory = {
	version: typeof ENTRIES_UNDO_SCHEMA_VERSION;
	undo: EntryUndoRecord[];
	redo: EntryUndoRecord[];
};

export type EntryRestoreResponse = {
	object: "entry_state_restore";
	reverseReceipt: string;
	action: string;
	label: string;
	affectedEntryGroupIds: string[];
	affectedEntryIds: string[];
	affectedEntryGroups: unknown[];
};

function isStringArray(value: unknown): value is string[] {
	return (
		Array.isArray(value) && value.every((item) => typeof item === "string")
	);
}

export function isEntryRestoreResponse(
	value: unknown,
): value is EntryRestoreResponse {
	if (!value || typeof value !== "object") return false;
	const candidate = value as Partial<EntryRestoreResponse>;
	return (
		candidate.object === "entry_state_restore" &&
		typeof candidate.reverseReceipt === "string" &&
		candidate.reverseReceipt.length > 0 &&
		typeof candidate.action === "string" &&
		typeof candidate.label === "string" &&
		isStringArray(candidate.affectedEntryGroupIds) &&
		isStringArray(candidate.affectedEntryIds) &&
		Array.isArray(candidate.affectedEntryGroups)
	);
}

export function isEntryUndoMetadata(
	value: unknown,
): value is EntryUndoMetadata {
	if (!value || typeof value !== "object") return false;
	const candidate = value as Partial<EntryUndoMetadata>;
	return (
		candidate.object === "entry_undo_metadata" &&
		typeof candidate.receipt === "string" &&
		candidate.receipt.length > 0 &&
		candidate.version === ENTRIES_UNDO_SCHEMA_VERSION &&
		typeof candidate.action === "string" &&
		typeof candidate.label === "string" &&
		Array.isArray(candidate.affectedEntryGroupIds) &&
		candidate.affectedEntryGroupIds.every((id) => typeof id === "string") &&
		Array.isArray(candidate.affectedEntryIds) &&
		candidate.affectedEntryIds.every((id) => typeof id === "string")
	);
}

export function findEntryUndoMetadata(
	value: unknown,
): EntryUndoMetadata | null {
	if (isEntryUndoMetadata(value)) return value;
	if (!value || typeof value !== "object") return null;
	const record = value as Record<string, unknown>;
	if (isEntryUndoMetadata(record.undo)) return record.undo;
	if (record.data !== value) return findEntryUndoMetadata(record.data);
	return null;
}
